import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

/**
 * OnboardingChecklist — widget de “próximos passos” para dashboards.
 *
 * Props:
 *  - title, subtitle: cabeçalho
 *  - items: [{ id, done, title, description, actionLabel?, onAction? }]
 *  - hideWhenComplete: se true (default), esconde-se quando tudo está done
 */
export function OnboardingChecklist({ title, subtitle, items, hideWhenComplete = true, testId }) {
  const total = items.length;
  const done = items.filter((i) => i.done).length;
  const percent = total === 0 ? 100 : Math.round((done / total) * 100);
  const complete = done === total;

  if (complete && hideWhenComplete) return null;

  return (
    <div data-testid={testId || "onboarding-checklist"}
      className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 mb-8">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600 mb-1">Começar bem</p>
          <h2 className="font-heading font-black tracking-tighter text-xl text-slate-900">{title}</h2>
          {subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="font-heading font-black tracking-tighter text-2xl text-slate-900" data-testid="onboarding-percent">{percent}%</p>
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{done} de {total}</p>
          </div>
        </div>
      </div>

      <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden mb-6">
        <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-500" style={{ width: `${percent}%` }} />
      </div>

      <ol className="space-y-3" data-testid="onboarding-items">
        {items.map((item, idx) => (
          <li key={item.id || idx}
            data-testid={`onboarding-item-${item.id || idx}`}
            className={`flex items-start gap-3 rounded-xl border p-4 transition-colors ${
              item.done
                ? "border-emerald-100 bg-emerald-50/40"
                : "border-black/5 bg-white hover:border-amber-200"
            }`}>
            <div className="pt-0.5 shrink-0">
              {item.done ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={2} />
              ) : (
                <Circle className="w-5 h-5 text-slate-300" strokeWidth={2} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`font-heading font-bold tracking-tight text-[15px] ${item.done ? "text-emerald-800 line-through decoration-emerald-300" : "text-slate-900"}`}>
                {item.title}
              </p>
              {item.description && (
                <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">{item.description}</p>
              )}
            </div>
            {!item.done && item.actionLabel && item.onAction && (
              <Button
                data-testid={`onboarding-action-${item.id || idx}`}
                size="sm" onClick={item.onAction}
                className="rounded-full bg-slate-900 hover:bg-slate-800 text-white shrink-0">
                {item.actionLabel} <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
